import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

const STORAGE_KEY = "theme";
const CLASS_LIGHT = "light-mode";
const CLASS_DARK = "dark-mode";

/** Tema padrão quando não há preferência salva ou em primeiro acesso. */
const DEFAULT_THEME = "light";

type Theme = "light" | "dark";

type ThemeContextValue = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getStoredTheme(): Theme {
	if (typeof window === "undefined") {
		return DEFAULT_THEME;
	}
	const stored = window.localStorage.getItem(STORAGE_KEY);
	if (stored === "light" || stored === "dark") {
		return stored;
	}
	return DEFAULT_THEME;
}

function applyTheme(theme: Theme) {
	if (typeof document === "undefined") {
		return;
	}
	const root = document.documentElement;
	root.classList.remove(CLASS_LIGHT, CLASS_DARK);
	root.classList.add(theme === "dark" ? CLASS_DARK : CLASS_LIGHT);
}

export function useTheme() {
	const ctx = useContext(ThemeContext);
	if (!ctx) {
		throw new Error("useTheme must be used within Theme provider");
	}
	return ctx;
}

export function Theme({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

	useEffect(() => {
		applyTheme(theme);
	}, [theme]);

	const setTheme = useCallback((next: Theme) => {
		setThemeState(next);
		if (typeof window !== "undefined") {
			window.localStorage.setItem(STORAGE_KEY, next);
			applyTheme(next);
		}
	}, []);

	return (
		<ThemeContext.Provider value={{ theme, setTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}
